import type { MapStyleKey } from './MapboxMap';

// ---------------------------------------------------------------------------
// The location-name caption for the Streets ("default") map style.
//
// Suburbs already carries its place name as a big label baked into the map
// itself (see searchedLabel.ts), so a separate caption would be redundant
// there. Streets has no such labelling.
//
// A white rounded-rect chip behind dark text (ported from an on-screen-only
// HTML overlay in App.tsx — see git history / PREVIEW-LABEL-WORK.md, then
// briefly replaced 2026-09-14 with plain light text and no box — reverted
// same day per a reference image showing the chip is the intended look).
// Sits directly above the attribution line (mapAttribution.ts), which
// reserves its own space at the very bottom of the frame first — the
// attribution itself is NOT on the chip, just plain text below it.
// ---------------------------------------------------------------------------

/** The caption's look, at the 1024px reference frame (scales
 *  proportionally for other output sizes). */
// Exact CSS spec (at the 1024px reference frame), from the design source:
//   padding: 4.335px 5.419px; gap: 1.355px; border-radius: 2.167px;
//   background: #FFF; color: #242424; font: 300 16.933px Roboto;
//   letter-spacing: 4.233px; text-transform: uppercase;
// `gap` isn't applicable — the chip has no other flex children to space
// from the single text run.
export const LOCATION_BADGE_STYLE = {
  font: 'Roboto',
  fontWeight: 300,
  fontSizePx: 16.933,
  letterSpacingPx: 4.233,
  textColor: '#242424',
  uppercase: true,
  boxColor: '#ffffff',
  boxPaddingXPx: 5.419,
  boxPaddingYPx: 4.335,
  boxCornerRadiusPx: 2.167,
  // Gap between the chip's own bottom edge and the top of whatever's
  // reserved below it (the attribution line) — exact spec, from the
  // design source's flex-column "gap: 2.709px" wrapping both chips.
  gapAbovePx: 2.709,
  // Keep at least this much clear on each side. A long name (e.g. "MOUNT
  // MAUNGANUI") at the base font size can run the chip edge-to-edge with
  // no margin at all — this is the guard for that.
  marginXPx: 64,
  // Never shrink smaller than this even if the name still doesn't fit —
  // at that point a slightly tight fit is better than illegibly small text.
  minFontSizePx: 8,
};

/** Whether a style shows this caption (Streets only — Suburbs has its own
 *  big label instead; see searchedLabel.ts's styleUsesSearchedLabel). */
export function styleUsesLocationBadge(styleKey: MapStyleKey): boolean {
  return styleKey === 'default';
}

async function ensureFontLoaded(spec: string): Promise<void> {
  if (typeof document === 'undefined' || !('fonts' in document)) return;
  try {
    await document.fonts.load(spec);
  } catch {
    // Best-effort — canvas falls back to the next font in the stack.
  }
}

/** Traces a rounded-rect as a single path (not four arcs + separate fill
 *  calls) so fill has no seam at large scale — a fill built from
 *  independently-drawn segments visibly cracked at print resolution. */
function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/** Draws the location-name caption (white chip, dark tracked-caps text)
 *  onto a size×size canvas 2D context, centred horizontally, its bottom
 *  edge `gapAbovePx` above `bottomLimit` (the Y coordinate returned by
 *  drawMapAttribution — keeps the two stacked without overlapping
 *  regardless of either one's own size). No-op if `label` is empty.
 *  Letter-spacing is applied by hand (drawing character by character)
 *  rather than via the CSS-only `ctx.letterSpacing` property, for
 *  compatibility across every browser this runs in (customer devices,
 *  Alister's admin Chrome). */
export async function drawLocationBadge(
  ctx: CanvasRenderingContext2D,
  size: number,
  label: string,
  bottomLimit: number,
): Promise<void> {
  const text = label.trim().toUpperCase();
  if (!text) return;

  const cfg = LOCATION_BADGE_STYLE;
  const scale = size / 1024;
  let fontSize = cfg.fontSizePx * scale;
  let letterSpacing = cfg.letterSpacingPx * scale;
  const minFontSize = cfg.minFontSizePx * scale;
  const paddingX = cfg.boxPaddingXPx * scale;
  const paddingY = cfg.boxPaddingYPx * scale;
  const cornerRadius = cfg.boxCornerRadiusPx * scale;
  const maxTextWidth = size - cfg.marginXPx * 2 * scale - paddingX * 2;
  await ensureFontLoaded(`${cfg.fontWeight} ${fontSize}px ${cfg.font}`);

  const gapAbove = cfg.gapAbovePx * scale;

  ctx.save();
  ctx.font = `${cfg.fontWeight} ${fontSize}px ${cfg.font}, Arial, sans-serif`;

  let charWidths = Array.from(text).map((ch) => ctx.measureText(ch).width);
  let trackedWidth =
    charWidths.reduce((a, b) => a + b, 0) + letterSpacing * Math.max(0, text.length - 1);

  // Long names can otherwise push the chip edge-to-edge (or off it
  // entirely) — shrink font size and tracking together, in proportion,
  // down to a floor, then re-measure once at that size.
  if (trackedWidth > maxTextWidth && fontSize > minFontSize) {
    const ratio = Math.max(maxTextWidth / trackedWidth, minFontSize / fontSize);
    fontSize *= ratio;
    letterSpacing *= ratio;
    ctx.font = `${cfg.fontWeight} ${fontSize}px ${cfg.font}, Arial, sans-serif`;
    charWidths = Array.from(text).map((ch) => ctx.measureText(ch).width);
    trackedWidth =
      charWidths.reduce((a, b) => a + b, 0) + letterSpacing * Math.max(0, text.length - 1);
  }

  const startX = (size - trackedWidth) / 2;

  // Position by the glyphs' actual rendered extent, not textBaseline
  // 'middle' — that centres on a font's built-in "middle" metric, which
  // for most fonts (Roboto included) sits noticeably off from the visual
  // extent of the caps it's drawing.
  ctx.textBaseline = 'alphabetic';
  const vMetrics = ctx.measureText(text);
  const ascent = vMetrics.actualBoundingBoxAscent || fontSize * 0.75;
  const descent = vMetrics.actualBoundingBoxDescent || fontSize * 0.2;

  const boxBottomY = bottomLimit - gapAbove;
  const boxHeight = ascent + descent + paddingY * 2;
  const boxTopY = boxBottomY - boxHeight;
  const boxLeftX = startX - paddingX;
  const boxWidth = trackedWidth + paddingX * 2;
  const baselineY = boxBottomY - paddingY - descent;

  ctx.fillStyle = cfg.boxColor;
  roundedRectPath(ctx, boxLeftX, boxTopY, boxWidth, boxHeight, cornerRadius);
  ctx.fill();

  ctx.fillStyle = cfg.textColor;
  let cx = startX;
  for (let i = 0; i < text.length; i++) {
    ctx.fillText(text[i], cx, baselineY);
    cx += charWidths[i] + letterSpacing;
  }
  ctx.restore();
}
